import { useEffect, useRef, useState } from "react";
import { api, fileUrl, formatApiErrorDetail } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { BadgeCheck, Loader2, Building2, MapPin, Hash, Percent, Landmark, Mail, Phone, ReceiptText, ImageUp, Trash2 } from "lucide-react";
import { motion } from "framer-motion";

const FIELDS = [
  { k: "company_name", label: "Raison sociale", icon: Building2, ph: "RADAR SAS" },
  { k: "legal_form", label: "Forme juridique", icon: Landmark, ph: "SAS, SARL, SASU…" },
  { k: "address", label: "Adresse du siège", icon: MapPin, ph: "10 rue de la Paix, 75002 Paris" },
  { k: "siren", label: "SIREN / SIRET", icon: Hash, ph: "123 456 789 00012" },
  { k: "vat_number", label: "N° TVA intracommunautaire", icon: Percent, ph: "FR12345678901" },
  { k: "capital", label: "Capital social", icon: Landmark, ph: "10 000 €" },
  { k: "email", label: "E-mail", icon: Mail, ph: "kazar.hamid@live.fr" },
  { k: "phone", label: "Téléphone", icon: Phone, ph: "+33 1 23 45 67 89" },
];
const EMPTY = Object.fromEntries(FIELDS.map((f) => [f.k, ""]));

export default function BillingSettings() {
  const [form, setForm] = useState(EMPTY);
  const [logoPath, setLogoPath] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const logoInputRef = useRef(null);

  const apply = (seller) => { setForm({ ...EMPTY, ...(seller || {}) }); setLogoPath((seller || {}).logo_path || ""); };

  useEffect(() => {
    api.get("/admin/billing-settings")
      .then((r) => apply(r.data.seller))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const isPlaceholder = (v) => !v || v.startsWith("À COMPLÉTER");

  const uploadLogo = async (file) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) return toast.error("Format image uniquement (PNG, JPG, WEBP).");
    setUploadingLogo(true);
    const fd = new FormData(); fd.append("file", file);
    try {
      const r = await api.post("/admin/billing-settings/logo", fd, { headers: { "Content-Type": "multipart/form-data" } });
      apply(r.data.seller);
      toast.success("Logo importé. Il apparaîtra en en-tête des factures PDF.");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setUploadingLogo(false); }
  };

  const removeLogo = async () => {
    try { const r = await api.delete("/admin/billing-settings/logo"); apply(r.data.seller); toast.success("Logo retiré."); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const save = async () => {
    setSaving(true);
    try {
      const payload = Object.fromEntries(FIELDS.map((f) => [f.k, isPlaceholder(form[f.k]) ? "" : form[f.k].trim()]));
      const r = await api.put("/admin/billing-settings", payload);
      apply(r.data.seller);
      toast.success("Paramètres de facturation enregistrés. Les prochaines factures utiliseront ces informations.");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally { setSaving(false); }
  };

  return (
    <div className="mx-auto max-w-3xl px-6 py-10 lg:px-12" data-testid="billing-settings-page">
      <header className="mb-8">
        <p className="overline text-[#C6A87C]">Super Administrateur</p>
        <h1 className="mt-2 font-display text-4xl tracking-tight text-[#0F172A] sm:text-5xl">Paramètres de facturation</h1>
        <p className="mt-2 text-slate-500">Identité légale de la société émettrice. Ces informations sont reprises automatiquement sur toutes les factures PDF.</p>
      </header>

      {loading ? (
        <div className="flex h-40 items-center justify-center text-slate-400"><Loader2 className="h-6 w-6 animate-spin" /></div>
      ) : (
        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }}
          className="rounded-2xl border border-slate-200 bg-white p-6 sm:p-8">
          <div className="mb-6 flex items-center gap-2 rounded-xl border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800" data-testid="billing-settings-hint">
            <ReceiptText className="h-4 w-4 shrink-0" />
            <span>Tant qu'un champ reste vide, la mention « À COMPLÉTER » apparaît sur les factures. Renseignez-les dès la création de la société.</span>
          </div>

          {/* Logo société */}
          <div className="mb-6 border-b border-slate-100 pb-6">
            <Label className="flex items-center gap-2 text-slate-700"><ImageUp className="h-4 w-4 text-[#C6A87C]" /> Logo de la société</Label>
            <div className="mt-2 flex flex-col gap-4 sm:flex-row sm:items-center">
              <div className="flex h-24 w-48 shrink-0 items-center justify-center overflow-hidden rounded-xl border border-dashed border-slate-300 bg-white" data-testid="billing-logo-preview">
                {logoPath ? (
                  <img src={fileUrl(logoPath)} alt="Logo société" className="max-h-20 max-w-[176px] object-contain" />
                ) : (
                  <span className="px-3 text-center text-xs text-slate-400">Aucun logo</span>
                )}
              </div>
              <div className="flex flex-wrap gap-2">
                <input ref={logoInputRef} type="file" accept="image/png,image/jpeg,image/webp" className="hidden" data-testid="billing-logo-input"
                  onChange={(e) => { if (e.target.files?.[0]) uploadLogo(e.target.files[0]); e.target.value = ""; }} />
                <Button variant="outline" onClick={() => logoInputRef.current?.click()} disabled={uploadingLogo} data-testid="billing-logo-upload-btn" className="rounded-full">
                  {uploadingLogo ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <ImageUp className="mr-2 h-4 w-4" />}
                  {logoPath ? "Remplacer le logo" : "Importer un logo"}
                </Button>
                {logoPath && (
                  <Button variant="outline" onClick={removeLogo} data-testid="billing-logo-remove-btn" className="rounded-full text-red-600 hover:text-red-700">
                    <Trash2 className="mr-2 h-4 w-4" /> Retirer
                  </Button>
                )}
              </div>
            </div>
            <p className="mt-1.5 text-xs text-slate-400">PNG, JPG ou WEBP. Le logo s'affiche automatiquement en en-tête de toutes les factures PDF.</p>
          </div>

          <div className="grid gap-5 sm:grid-cols-2">
            {FIELDS.map((f) => (
              <div key={f.k} className={f.k === "address" ? "sm:col-span-2" : ""}>
                <Label className="flex items-center gap-2 text-slate-700"><f.icon className="h-4 w-4 text-[#C6A87C]" /> {f.label}</Label>
                <Input
                  data-testid={`billing-${f.k}`}
                  value={isPlaceholder(form[f.k]) ? "" : form[f.k]}
                  onChange={(e) => set(f.k, e.target.value)}
                  placeholder={f.ph}
                  className="mt-1.5 h-11"
                />
              </div>
            ))}
          </div>
          <div className="mt-8 flex justify-end">
            <Button onClick={save} disabled={saving} data-testid="billing-save-btn" className="rounded-full bg-[#0F172A] px-6 text-white hover:bg-[#1e293b]">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : (<><BadgeCheck className="mr-2 h-4 w-4" /> Enregistrer</>)}
            </Button>
          </div>
        </motion.div>
      )}
    </div>
  );
}
